import type { ReactElement } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  DashboardSignalPanel,
  SignalRow,
} from "@/modules/dashboard/components/redesign/DashboardSignalPanel";
import {
  MoneyValue,
  SIGNAL_UNIT_STYLE,
} from "@/modules/dashboard/components/redesign/MoneyValue";
import type { DashboardDiscountImpactPanelProps } from "@/modules/dashboard/types/dashboard-analytics.types";

/**
 * Discount return.
 *
 * The verdict line is the point of the panel: a discount only pays for itself
 * if the discounted basket is BIGGER than the undiscounted one. It is stated
 * only when there is an undiscounted baseline to compare against — with no
 * undiscounted orders in the range the comparison is meaningless and claiming
 * either verdict would be inventing a finding.
 */
export function DashboardDiscountImpactPanel({
  impact,
  formatValue,
  formatValueParts,
  formatPercent,
}: DashboardDiscountImpactPanelProps): ReactElement {
  const t = useTranslations();
  const isEmpty = impact.ordersWithDiscount === 0;
  const hasBaseline = impact.avgOrderWithoutDiscount > 0;
  const liftsBasket = impact.avgOrderWithDiscount > impact.avgOrderWithoutDiscount;

  return (
    <DashboardSignalPanel
      title={t(keys.dashboard.discTitle)}
      subtitle={t(keys.dashboard.discSubtitle)}
      isEmpty={isEmpty}
      emptyTitle={t(keys.dashboard.discEmptyTitle)}
      emptyBody={t(keys.dashboard.discEmptyBody)}
    >
      <div className="flex items-baseline gap-2">
        <span className="truncate text-[22px] font-bold leading-[1.3] text-[color:var(--text-primary)]">
          {/* Panel headline — unit subordinate. The label/value rows below keep
              the joined string: at 12.5px they are body text, not a headline. */}
          <MoneyValue
            {...formatValueParts(impact.totalDiscounted)}
            unitStyle={SIGNAL_UNIT_STYLE}
          />
        </span>
        {/* The headline money carries its order count, matching the loss
            panel's "N ج.م across M orders" pattern — a discount total says
            nothing until you know how many baskets it was spread across. */}
        <span className="shrink-0 text-[11.5px] text-[color:var(--text-muted)]">
          {t(keys.dashboard.discGivenAway, {
            count: impact.ordersWithDiscount,
          })}
        </span>
      </div>

      <div className="mt-3">
        <SignalRow
          label={t(keys.dashboard.discShare)}
          value={formatPercent(impact.shareOfSales)}
        />
        <SignalRow
          label={t(keys.dashboard.discOrders)}
          value={impact.ordersWithDiscount.toLocaleString()}
        />
        <SignalRow
          label={t(keys.dashboard.discAovWith)}
          value={formatValue(impact.avgOrderWithDiscount)}
          strong
        />
        <SignalRow
          label={t(keys.dashboard.discAovWithout)}
          value={
            hasBaseline
              ? formatValue(impact.avgOrderWithoutDiscount)
              : t(keys.dashboard.discNoBaseline)
          }
        />
      </div>

      {hasBaseline ? (
        <p
          className="mt-2 rounded-[8px] px-2.5 py-2 text-[11.5px] leading-[1.5]"
          style={{
            background: liftsBasket ? "var(--success-100)" : "var(--error-100)",
            color: liftsBasket ? "var(--success-text)" : "var(--error-600)",
          }}
        >
          {liftsBasket
            ? t(keys.dashboard.discVerdictGood)
            : t(keys.dashboard.discVerdictBad)}
        </p>
      ) : null}

      {impact.topPromotion ? (
        <div className="mt-3 border-t border-[color:var(--line)] pt-2">
          <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.03em] text-[color:var(--text-muted)]">
            {t(keys.dashboard.discTopPromotion)}
          </p>
          <SignalRow
            // An automatic promotion has no code the buyer ever types.
            label={
              impact.topPromotion.code ?? t(keys.dashboard.discAutomatic)
            }
            value={formatValue(impact.topPromotion.amount)}
            // Money + the orders it was spent on, never money alone.
            meta={t(keys.dashboard.ordersCount, {
              count: impact.topPromotion.orders,
            })}
            strong
          />
        </div>
      ) : null}
    </DashboardSignalPanel>
  );
}
